/**
 * Photo gallery slider for the about page template.
 *
 * @package LecardoVein
 */

import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';

/**
 * @param {HTMLElement} root
 * @return {void}
 */
function bindPhotoGalleryFancybox( root ) {
	Fancybox.bind( root, 'a[data-fancybox]', {
		Carousel: {
			infinite: true,
		},
	} );
}

/**
 * @param {HTMLElement} root
 */
function initPhotoGallery( root ) {
	bindPhotoGalleryFancybox( root );
	const track = root.querySelector( '[data-photo-gallery-track]' );

	if ( ! track ) {
		return;
	}

	const slides = Array.from( track.children );

	if ( ! slides.length ) {
		return;
	}

	const viewport = root.querySelector( '[data-photo-gallery-viewport]' );
	const prevButton = root.querySelector( '[data-photo-gallery-prev]' );
	const nextButton = root.querySelector( '[data-photo-gallery-next]' );
	const pagination = root.querySelector( '[data-photo-gallery-pagination]' );
	const pageLabelTemplate = pagination?.getAttribute( 'data-page-label' ) || 'Go to page %d';

	let index = 0;
	let slidesPerView = 1;
	let maxIndex = 0;
	let swipeStartX = 0;
	let swipeStartY = 0;
	let isSwiping = false;
	let didSwipe = false;
	let activePointerId = null;

	const SWIPE_THRESHOLD = 40;

	/**
	 * @return {boolean}
	 */
	function isDesktop() {
		return window.matchMedia( '(min-width: 64rem)' ).matches;
	}

	/**
	 * @return {void}
	 */
	function updateMetrics() {
		slidesPerView = isDesktop() ? Math.min( 4, slides.length ) : 1;
		maxIndex = Math.max( 0, slides.length - slidesPerView );
		index = Math.min( index, maxIndex );
	}

	/**
	 * @return {void}
	 */
	function renderDots() {
		if ( ! pagination ) {
			return;
		}

		const pageCount = maxIndex + 1;

		if ( pagination.children.length !== pageCount ) {
			pagination.innerHTML = '';

			for ( let pageIndex = 0; pageIndex < pageCount; pageIndex += 1 ) {
				const dot = document.createElement( 'button' );

				dot.type = 'button';
				dot.className = 'photo-gallery__dot';
				dot.setAttribute( 'data-photo-gallery-dot', String( pageIndex ) );
				dot.setAttribute(
					'aria-label',
					pageLabelTemplate.replace( '%d', String( pageIndex + 1 ) )
				);
				dot.addEventListener( 'click', () => {
					goTo( pageIndex );
				} );
				pagination.appendChild( dot );
			}
		}

		Array.from( pagination.children ).forEach( ( dot, dotIndex ) => {
			dot.classList.toggle( 'is-active', dotIndex === index );
			dot.setAttribute( 'aria-current', dotIndex === index ? 'true' : 'false' );
		} );
	}

	/**
	 * @return {void}
	 */
	function updateButtons() {
		if ( prevButton ) {
			prevButton.disabled = index <= 0;
		}

		if ( nextButton ) {
			nextButton.disabled = index >= maxIndex;
		}
	}

	/**
	 * @return {void}
	 */
	function updatePosition() {
		const slide = slides[ 0 ];
		const styles = window.getComputedStyle( track );
		const gap = parseFloat( styles.columnGap || styles.gap || '0' ) || 0;
		const offset = index * ( slide.offsetWidth + gap );

		track.style.transform = `translate3d(-${offset}px, 0, 0)`;
		renderDots();
		updateButtons();
	}

	/**
	 * @param {number} nextIndex
	 * @return {void}
	 */
	function goTo( nextIndex ) {
		index = Math.max( 0, Math.min( nextIndex, maxIndex ) );
		updatePosition();
	}

	/**
	 * @return {void}
	 */
	function handleResize() {
		updateMetrics();
		renderDots();
		updatePosition();
	}

	if ( prevButton ) {
		prevButton.addEventListener( 'click', () => {
			goTo( index - 1 );
		} );
	}

	if ( nextButton ) {
		nextButton.addEventListener( 'click', () => {
			goTo( index + 1 );
		} );
	}

	/**
	 * @param {number} clientX
	 * @param {number} clientY
	 * @return {void}
	 */
	function onSwipeStart( clientX, clientY ) {
		swipeStartX = clientX;
		swipeStartY = clientY;
		isSwiping = true;
		didSwipe = false;
	}

	/**
	 * @param {number} clientX
	 * @param {number} clientY
	 * @return {void}
	 */
	function onSwipeEnd( clientX, clientY ) {
		if ( ! isSwiping ) {
			return;
		}

		isSwiping = false;

		const deltaX = clientX - swipeStartX;
		const deltaY = clientY - swipeStartY;

		if ( Math.abs( deltaX ) < SWIPE_THRESHOLD || Math.abs( deltaX ) < Math.abs( deltaY ) ) {
			return;
		}

		didSwipe = true;

		if ( deltaX < 0 ) {
			goTo( index + 1 );
			return;
		}

		goTo( index - 1 );
	}

	if ( viewport ) {
		/**
		 * @param {PointerEvent} event
		 * @return {void}
		 */
		function onPointerDown( event ) {
			if ( event.pointerType === 'mouse' && event.button !== 0 ) {
				return;
			}

			if ( null !== activePointerId ) {
				return;
			}

			activePointerId = event.pointerId;
			viewport.setPointerCapture( event.pointerId );
			viewport.classList.add( 'is-dragging' );
			onSwipeStart( event.clientX, event.clientY );
		}

		/**
		 * @param {PointerEvent} event
		 * @return {void}
		 */
		function onPointerMove( event ) {
			if ( event.pointerId !== activePointerId || ! isSwiping ) {
				return;
			}

			const deltaX = event.clientX - swipeStartX;
			const deltaY = event.clientY - swipeStartY;

			if ( Math.abs( deltaX ) > Math.abs( deltaY ) && Math.abs( deltaX ) > 10 ) {
				event.preventDefault();
			}
		}

		/**
		 * @param {PointerEvent} event
		 * @return {void}
		 */
		function finishPointer( event ) {
			if ( event.pointerId !== activePointerId ) {
				return;
			}

			if ( viewport.hasPointerCapture( event.pointerId ) ) {
				viewport.releasePointerCapture( event.pointerId );
			}

			activePointerId = null;
			viewport.classList.remove( 'is-dragging' );

			if ( 'pointercancel' === event.type ) {
				isSwiping = false;
				return;
			}

			onSwipeEnd( event.clientX, event.clientY );
		}

		viewport.addEventListener( 'pointerdown', onPointerDown );
		viewport.addEventListener( 'pointermove', onPointerMove, { passive: false } );
		viewport.addEventListener( 'pointerup', finishPointer );
		viewport.addEventListener( 'pointercancel', finishPointer );

		viewport.addEventListener(
			'click',
			( event ) => {
				if ( ! didSwipe ) {
					return;
				}

				event.preventDefault();
				event.stopPropagation();
				didSwipe = false;
			},
			true
		);

		viewport.addEventListener( 'dragstart', ( event ) => {
			event.preventDefault();
		} );
	}

	window.addEventListener( 'resize', handleResize );
	handleResize();
}

/**
 * @return {void}
 */
function initPhotoGalleries() {
	document.querySelectorAll( '[data-photo-gallery]' ).forEach( initPhotoGallery );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initPhotoGalleries );
} else {
	initPhotoGalleries();
}
